/**
 * Discards baggage selections written before checked baggage was keyed by leg.
 *
 * Baggage selections are stored `legId -> travellerId -> selection`, where `legId` is the leg's
 * `selectedFareId`. An earlier client keyed the same map by a flight id, and such a record is
 * unresolvable now: the modal seeds nothing from it, so it opens with the bag shown as unselected,
 * while the checkout summary still sums its `amount` — the customer is charged for a bag the UI says
 * they do not have. Dropping the record makes both agree on zero, which is the only safe direction:
 * an unsold bag is recoverable by the customer, a silent charge is not.
 *
 * A legacy record is identified structurally rather than by a version stamp, since none was ever
 * written: its map key is a flight id and so cannot equal its own `selectedFareId`, and it carries
 * no `availabilityLegId`. Both conditions are checked — either alone makes the record unusable.
 *
 * This must be applied wherever selections are read, and specifically *before* any total is derived
 * from them. It is applied inside `getFlightBaggageSelectionsByKey`, the single selector feeding both
 * the modal and the summary, so the ordering holds by construction rather than by convention.
 */
export function pruneLegacyBaggageSelections(
  selectionsByKey: Record<string, Record<string, Record<string, App.FlightBaggageSelection>>>,
): Record<string, Record<string, Record<string, App.FlightBaggageSelection>>> {
  const pruned: Record<string, Record<string, Record<string, App.FlightBaggageSelection>>> = {}

  for (const [fareKey, selectionsByLeg] of Object.entries(selectionsByKey)) {
    const prunedLegs: Record<string, Record<string, App.FlightBaggageSelection>> = {}

    for (const [legId, travellerSelections] of Object.entries(selectionsByLeg ?? {})) {
      const prunedTravellers: Record<string, App.FlightBaggageSelection> = {}

      for (const [travellerId, selection] of Object.entries(travellerSelections ?? {})) {
        if (!isLegKeyedSelection(legId, selection)) { continue }
        prunedTravellers[travellerId] = selection
      }

      if (Object.keys(prunedTravellers).length > 0) {
        prunedLegs[legId] = prunedTravellers
      }
    }

    pruned[fareKey] = prunedLegs
  }

  return pruned
}

function isLegKeyedSelection(legId: string, selection: App.FlightBaggageSelection | undefined): boolean {
  if (!selection) { return false }
  return selection.selectedFareId === legId && Boolean(selection.availabilityLegId)
}
